import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Card, Select, Drawer, InputNumber, Switch, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import { useCanDelete } from '../hooks/useCanDelete';
import {
  fetchCategoryAttributes,
  createCategoryAttribute,
  updateCategoryAttribute,
  deleteCategoryAttribute,
  AttributeDataType,
  type CategoryAttributeDto,
  type CreateCategoryAttributeRequest,
  type UpdateCategoryAttributeRequest,
} from '../services/categoryAttributeService';

interface Category {
  id: number;
  categoryCode: string;
  categoryName: string;
  parentId?: number | null;
  parent?: Category;
  children?: Category[];
  isActive: boolean;
  createdAt: string;
  level?: number; // Hiyerarşi seviyesi (0: ana kategori, 1: alt kategori, 2: alt-alt kategori...)
}

const DATA_TYPE_LABELS: Record<AttributeDataType, string> = {
  [AttributeDataType.Number]: 'Sayı',
  [AttributeDataType.Text]: 'Metin',
  [AttributeDataType.Date]: 'Tarih',
};

const Categories: React.FC = () => {
  const canDelete = useCanDelete();
  const [categories, setCategories] = useState<Category[]>([]);
  const [originalCategories, setOriginalCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [form] = Form.useForm();

  // Kategori öznitelikleri drawer
  const [attributesDrawerVisible, setAttributesDrawerVisible] = useState(false);
  const [attributesCategory, setAttributesCategory] = useState<Category | null>(null);
  const [categoryAttributes, setCategoryAttributes] = useState<CategoryAttributeDto[]>([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [attributeForm] = Form.useForm();
  const [editingAttribute, setEditingAttribute] = useState<CategoryAttributeDto | null>(null);
  const [attributeModalVisible, setAttributeModalVisible] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const { data } = await api.get<Category[]>('/categories');
      console.log('Categories loaded:', data);
      
      setOriginalCategories(data); // Orijinal veriyi sakla
      
      // Hiyerarşik sıralama: önce ana kategoriler, sonra alt kategoriler
      const sortedCategories = sortCategoriesHierarchically(data);
      setCategories(sortedCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
      message.error('Kategoriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Kategorileri recursive olarak hiyerarşik sırala
  const sortCategoriesHierarchically = (cats: Category[]): Category[] => {
    const result: Category[] = [];
    
    // Recursive fonksiyon: bir kategorinin tüm alt ağacını ekle
    const addCategoryAndChildren = (categoryId: number, level: number = 0) => {
      const category = cats.find(c => c.id === categoryId);
      if (!category) return;
      
      // Seviye bilgisini kategoriye ekle (render için)
      const categoryWithLevel = { ...category, level };
      result.push(categoryWithLevel as any);
      
      // Bu kategorinin direkt çocuklarını bul ve recursive ekle
      const children = cats.filter(c => c.parentId === categoryId);
      children.forEach(child => addCategoryAndChildren(child.id, level + 1));
    };
    
    // Kök kategorilerden başla (parentId null olanlar)
    const rootCategories = cats.filter(cat => !cat.parentId);
    rootCategories.forEach(root => addCategoryAndChildren(root.id, 0));
    
    return result;
  };

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Category) => {
    setEditingCategory(record);
    form.setFieldsValue({
      categoryCode: record.categoryCode,
      categoryName: record.categoryName,
      parentId: record.parentId || undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await api.delete(`/categories/${id}`);
      message.success('Kategori başarıyla silindi');
      loadCategories();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Kategori silinirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, {
          id: editingCategory.id,
          ...values,
        });
        message.success('Kategori başarıyla güncellendi');
      } else {
        await api.post('/categories', values);
        message.success('Kategori başarıyla oluşturuldu');
      }

      setModalVisible(false);
      form.resetFields();
      loadCategories();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem sırasında hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingCategory(null);
  };

  const openAttributesDrawer = async (record: Category) => {
    setAttributesCategory(record);
    setAttributesDrawerVisible(true);
    setEditingAttribute(null);
    setAttributeModalVisible(false);
    attributeForm.resetFields();
    await loadCategoryAttributes(record.id);
  };

  const loadCategoryAttributes = async (categoryId: number) => {
    try {
      setLoadingAttributes(true);
      const list = await fetchCategoryAttributes(categoryId);
      setCategoryAttributes(list);
    } catch (error) {
      console.error('Öznitelikler yüklenirken hata:', error);
      message.error('Öznitelikler yüklenirken hata oluştu');
      setCategoryAttributes([]);
    } finally {
      setLoadingAttributes(false);
    }
  };

  const handleAddAttribute = () => {
    setEditingAttribute(null);
    attributeForm.resetFields();
    attributeForm.setFieldsValue({ isFilterable: true, sortOrder: 0, dataType: AttributeDataType.Text });
    setAttributeModalVisible(true);
  };

  const handleEditAttribute = (attr: CategoryAttributeDto) => {
    setEditingAttribute(attr);
    attributeForm.setFieldsValue({
      attributeCode: attr.attributeCode,
      name: attr.name,
      dataType: attr.dataType,
      unit: attr.unit,
      isFilterable: attr.isFilterable,
      sortOrder: attr.sortOrder,
    });
    setAttributeModalVisible(true);
  };

  const handleAttributeModalOk = async () => {
    if (!attributesCategory) return;
    try {
      const values = await attributeForm.validateFields();
      setLoadingAttributes(true);
      if (editingAttribute) {
        await updateCategoryAttribute(editingAttribute.id, {
          id: editingAttribute.id,
          attributeCode: values.attributeCode,
          name: values.name,
          dataType: values.dataType,
          unit: values.unit || undefined,
          isFilterable: values.isFilterable ?? true,
          sortOrder: values.sortOrder ?? 0,
        });
        message.success('Öznitelik güncellendi');
      } else {
        await createCategoryAttribute({
          categoryId: attributesCategory.id,
          attributeCode: values.attributeCode,
          name: values.name,
          dataType: values.dataType,
          unit: values.unit || undefined,
          isFilterable: values.isFilterable ?? true,
          sortOrder: values.sortOrder ?? 0,
        });
        message.success('Öznitelik eklendi');
      }
      setAttributeModalVisible(false);
      attributeForm.resetFields();
      await loadCategoryAttributes(attributesCategory.id);
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.response?.data?.message || 'İşlem sırasında hata oluştu');
    } finally {
      setLoadingAttributes(false);
    }
  };

  const handleDeleteAttribute = async (id: number) => {
    if (!attributesCategory) return;
    try {
      setLoadingAttributes(true);
      await deleteCategoryAttribute(id);
      message.success('Öznitelik silindi');
      await loadCategoryAttributes(attributesCategory.id);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Silinirken hata oluştu');
    } finally {
      setLoadingAttributes(false);
    }
  };

  // Üst kategori olabilecek kategoriler (düzenleme sırasında kendisi ve alt kategorileri hariç)
  const getAvailableParentCategories = () => {
    if (!editingCategory) {
      return originalCategories;
    }
    
    // Düzenlenen kategorinin tüm alt kategorilerini recursive olarak bul
    const getAllDescendantIds = (catId: number): number[] => {
      const children = originalCategories.filter(c => c.parentId === catId);
      let descendantIds = children.map(c => c.id);
      children.forEach(child => {
        descendantIds = [...descendantIds, ...getAllDescendantIds(child.id)];
      });
      return descendantIds;
    };
    
    const excludeIds = [editingCategory.id, ...getAllDescendantIds(editingCategory.id)];
    return originalCategories.filter(cat => !excludeIds.includes(cat.id));
  };

  const columns = [
    {
      title: 'Kategori Kodu',
      dataIndex: 'categoryCode',
      key: 'categoryCode',
    },
    {
      title: 'Kategori Adı',
      dataIndex: 'categoryName',
      key: 'categoryName',
      render: (text: string, record: Category) => {
        const level = record.level || 0;
        
        // Ana kategori (level 0)
        if (level === 0) {
          return (
            <strong style={{ color: '#1890ff', fontSize: '15px' }}>
              📁 {text}
            </strong>
          );
        }
        
        // Alt kategoriler - seviyeye göre girinti ve işaret
        const indent = level * 20; // Her seviye için 20px girinti
        
        // Seviyeye göre farklı connector işaretleri
        const getConnector = (lvl: number) => {
          const lines = '│   '.repeat(Math.max(0, lvl - 1)); // Dikey çizgiler
          const branch = lvl > 0 ? '├─ ' : '';
          return lines + branch;
        };
        
        // Seviyeye göre renk tonu
        const getColor = (lvl: number) => {
          const colors = ['#262626', '#595959', '#8c8c8c', '#bfbfbf'];
          return colors[Math.min(lvl - 1, colors.length - 1)];
        };
        
        return (
          <span style={{ paddingLeft: `${indent}px`, display: 'block', fontFamily: 'monospace' }}>
            <span style={{ color: '#999', marginRight: '4px' }}>{getConnector(level)}</span>
            <span style={{ color: getColor(level) }}>{text}</span>
          </span>
        );
      },
    },
    {
      title: 'Üst Kategori',
      dataIndex: 'parentId',
      key: 'parentId',
      render: (parentId: number) => {
        if (!parentId) return <span style={{ color: '#999' }}>-</span>;
        const parent = originalCategories.find(c => c.id === parentId);
        return parent ? <span style={{ color: '#52c41a' }}>{parent.categoryName}</span> : '-';
      },
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? 'green' : 'red' }}>
          {isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
      filters: [
        { text: 'Aktif', value: true },
        { text: 'Pasif', value: false },
      ],
      onFilter: (value: any, record: Category) => record.isActive === value,
    },
    {
      title: 'Oluşturma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => {
        if (!date) return '-';
        try {
          const d = new Date(date);
          return d.toLocaleDateString('tr-TR', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch {
          return '-';
        }
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 280,
      render: (_: any, record: Category) => (
        <Space size="small">
          <Button
            type="link"
            icon={<AppstoreOutlined />}
            onClick={() => openAttributesDrawer(record)}
            title="Kategori öznitelikleri (genişlik, yükseklik, üretim yılı vb.)"
          >
            Öznitelikler
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          {canDelete && (
          <Popconfirm
            title="Bu kategoriyi silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Kategori Yönetimi"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Yeni Kategori Ekle
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={categories}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Toplam ${total} kategori`,
        }}
        rowClassName={(record) => {
          const level = record.level || 0;
          if (level === 0) return 'parent-category-row';
          if (level === 1) return 'sub-category-row';
          return 'sub-sub-category-row';
        }}
      />

      <Modal
        title={editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori Ekle'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        okText="Kaydet"
        cancelText="İptal"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          name="categoryForm"
        >
          <Form.Item
            name="categoryCode"
            label="Kategori Kodu"
            rules={[
              { required: true, message: 'Kategori kodu gerekli' },
              { max: 50, message: 'Kategori kodu en fazla 50 karakter olabilir' },
            ]}
          >
            <Input placeholder="Örn: KAT001" />
          </Form.Item>

          <Form.Item
            name="categoryName"
            label="Kategori Adı"
            rules={[
              { required: true, message: 'Kategori adı gerekli' },
              { max: 200, message: 'Kategori adı en fazla 200 karakter olabilir' },
            ]}
          >
            <Input placeholder="Örn: Elektronik" />
          </Form.Item>

          <Form.Item
            name="parentId"
            label="Üst Kategori"
            tooltip="Bu kategori bir alt kategori ise üst kategorisini seçin"
          >
            <Select
              placeholder="Üst kategori seçiniz (isteğe bağlı)"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const searchText = `${option?.children}`.toLowerCase();
                return searchText.includes(input.toLowerCase());
              }}
            >
              {sortCategoriesHierarchically(getAvailableParentCategories()).map(cat => {
                const level = cat.level || 0;
                const indent = '　'.repeat(level); // Full-width space for indentation
                const prefix = level > 0 ? '└─ ' : '';
                
                return (
                  <Select.Option 
                    key={cat.id} 
                    value={cat.id}
                  >
                    {indent}{prefix}{cat.categoryName} <span style={{ color: '#999' }}>({cat.categoryCode})</span>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={`Kategori öznitelikleri: ${attributesCategory?.categoryName ?? ''}`}
        placement="right"
        width={560}
        onClose={() => { setAttributesDrawerVisible(false); setAttributesCategory(null); }}
        open={attributesDrawerVisible}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAttribute}>
            Öznitelik Ekle
          </Button>
        }
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          Bu kategorideki ürünlerde kullanılacak alanları tanımlayın. Üst kategoride tanımlı öznitelikler otomatik miras alınır (alt kategoride tekrar eklemenize gerek yok). Filtrelenebilir işaretlenenler ürün listesinde filtre olarak kullanılır.
        </p>
        <Table
          dataSource={categoryAttributes}
          rowKey="id"
          loading={loadingAttributes}
          size="small"
          columns={[
            { title: 'Kod', dataIndex: 'attributeCode', key: 'attributeCode', width: 100 },
            {
              title: 'Ad',
              key: 'name',
              render: (_: any, row: CategoryAttributeDto) => (
                <Space>
                  <span>{row.name}</span>
                  {(row.isInherited || (attributesCategory && row.sourceCategoryId !== attributesCategory.id)) && (
                    <Tag color="blue">Üst kategoriden miras</Tag>
                  )}
                </Space>
              ),
            },
            {
              title: 'Tip',
              dataIndex: 'dataType',
              key: 'dataType',
              width: 80,
              render: (t: AttributeDataType) => DATA_TYPE_LABELS[t] ?? t,
            },
            { title: 'Birim', dataIndex: 'unit', key: 'unit', width: 60 },
            {
              title: 'Filtre',
              dataIndex: 'isFilterable',
              key: 'isFilterable',
              width: 60,
              render: (v: boolean) => (v ? 'Evet' : 'Hayır'),
            },
            {
              title: 'İşlem',
              key: 'action',
              width: 100,
              render: (_: any, row: CategoryAttributeDto) => {
                const isOwn = attributesCategory && row.sourceCategoryId === attributesCategory.id;
                if (!isOwn) {
                  return <span style={{ color: '#999', fontSize: 12 }}>Miras – düzenlenemez</span>;
                }
                return (
                  <Space>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditAttribute(row)} />
                    {canDelete && (
                    <Popconfirm
                      title="Bu özniteliği silmek istediğinize emin misiniz?"
                      onConfirm={() => handleDeleteAttribute(row.id)}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                    )}
                  </Space>
                );
              },
            },
          ]}
          pagination={false}
        />
      </Drawer>

      <Modal
        title={editingAttribute ? 'Öznitelik Düzenle' : 'Yeni Öznitelik Ekle'}
        open={attributeModalVisible}
        onOk={handleAttributeModalOk}
        onCancel={() => { setAttributeModalVisible(false); setEditingAttribute(null); attributeForm.resetFields(); }}
        confirmLoading={loadingAttributes}
        okText="Kaydet"
        cancelText="İptal"
        width={440}
      >
        <Form form={attributeForm} layout="vertical">
          <Form.Item name="attributeCode" label="Öznitelik Kodu" rules={[{ required: true }, { max: 50 }]}>
            <Input placeholder="Örn: WIDTH, HEIGHT, PRODUCTION_YEAR" />
          </Form.Item>
          <Form.Item name="name" label="Görünen Ad" rules={[{ required: true }, { max: 200 }]}>
            <Input placeholder="Örn: Genişlik, Yükseklik, Üretim Yılı" />
          </Form.Item>
          <Form.Item name="dataType" label="Veri Tipi" rules={[{ required: true }]}>
            <Select
              options={[
                { value: AttributeDataType.Number, label: 'Sayı' },
                { value: AttributeDataType.Text, label: 'Metin' },
                { value: AttributeDataType.Date, label: 'Tarih' },
              ]}
            />
          </Form.Item>
          <Form.Item name="unit" label="Birim (opsiyonel)">
            <Input placeholder="Örn: mm, inch, yıl" maxLength={20} />
          </Form.Item>
          <Form.Item name="isFilterable" label="Filtrelemede kullan" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sıra">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Categories;
